/* global runtime */
// Skattemeldingsmodell, satser og lagring av fremdrift.
// Lastes som klassisk script før backend.jsx og deler dens bindingsmiljø.
const NO_CORPORATE_TAX_RATES = [
  { effectiveFrom: 2019, effectiveTo: null, rate: 0.22 },
  { effectiveFrom: 2018, effectiveTo: 2018, rate: 0.23 },
  { effectiveFrom: 2017, effectiveTo: 2017, rate: 0.24 },
  { effectiveFrom: 2016, effectiveTo: 2016, rate: 0.25 },
];
const norwegianCorporateTaxRate = (fiscalYear) => {
  const year = Math.trunc(Number(fiscalYear));
  if (!Number.isInteger(year)) return null;
  const match = NO_CORPORATE_TAX_RATES.find((entry) =>
    year >= entry.effectiveFrom && (entry.effectiveTo === null || year <= entry.effectiveTo));
  return match ? match.rate : null;
};
// The tax-basis calculation itself lives in src/services/taxBasisCalculation.ts,
// shared with the tax return page so both show the same figures.

const IMPORTED_TAX_RETURN_STEP_IDS = ['summary', 'tax-base', 'wealth', 'assets', 'other', 'income-balance', 'all'];
const IMPORTED_ANNUAL_ACCOUNTS_STEP_IDS = ['about', 'income', 'balance', 'notes', 'all'];
const IMPORTED_YEAR_CLOSING_CHECK_IDS = [
  'custom', 'share', 'bank', 'cash', 'vat-returns', 'vat-cleanup',
  'pension', 'purchases', 'sales', 'inventory',
  'payables', 'receivables', 'fixed-assets', 'payroll', 'equity-review', 'tax',
];
const importedComplianceDelivered = (state, year, decision) =>
  state.accountingImportComplianceByYear?.[String(year)]?.decisions?.[decision] === 'delivered';

// Read-only book-value preview; statutory wealth valuations remain subject to review.
const isShareInvestmentAccount = (row) => {
  const label = String(row.label || '').toLowerCase();
  const nr = Number(row.code);
  return (nr >= 1300 && nr <= 1399) ||
    (nr >= 1800 && nr <= 1899) ||
    /\b(aksje|aksjer|andel|andeler|verdipapir|fond|investering)\b/i.test(label);
};

const buildWealthRow = (row, { absolute = false, negate = false } = {}) => ({
  code: row.code,
  label: row.label,
  amount: negate ? -num(row.amount) : absolute ? Math.abs(row.amount) : row.amount,
  source: 'trial-balance',
});

const sumWealthRows = (rows) =>
  Math.round(safeArray(rows).reduce((sum, row) => sum + num(row.amount), 0) * 100) / 100;

const taxReturnWealthModel = (reports, isEnk) => {
  const allAccounts = trialAccountRows(reports);
  const assetAccounts = allAccounts.filter((row) => isTrialAccountInRange(row, 1000, 1999));
  const debtAccounts = allAccounts.filter((row) => isTrialAccountInRange(row, 2100, 2999) && !['2120', '2500'].includes(String(row.code).slice(0, 4)));
  const assetInventoryRows = assetAccounts
    .filter((row) => isTrialAccountInRange(row, 1000, 1299) || isTrialAccountInRange(row, 1400, 1499))
    .map((row) => buildWealthRow(row));
  const sharesInvestmentRows = assetAccounts
    .filter((row) => !assetInventoryRows.some((assetRow) => assetRow.code === row.code) && isShareInvestmentAccount(row))
    .map((row) => buildWealthRow(row));
  const classifiedCodes = new Set([
    ...assetInventoryRows.map((row) => row.code),
    ...sharesInvestmentRows.map((row) => row.code),
  ]);
  const otherAssetRows = assetAccounts
    .filter((row) => !classifiedCodes.has(row.code))
    .concat(debtAccounts.filter((row) => row.amount > 0))
    .map((row) => buildWealthRow(row));
  const debtRows = debtAccounts.filter((row) => row.amount < 0).map((row) => buildWealthRow(row, { negate: true }));
  const assetsInventoryTotal = sumWealthRows(assetInventoryRows);
  const sharesInvestmentTotal = sumWealthRows(sharesInvestmentRows);
  const otherAssetsTotal = sumWealthRows(otherAssetRows);
  const totalAssets = Math.max(0, assetsInventoryTotal + sharesInvestmentTotal + otherAssetsTotal);
  const totalDebt = sumWealthRows(debtRows);
  const netWealth = Math.round((totalAssets - totalDebt) * 100) / 100;
  return {
    receivables: Math.max(0, trialAccountValue(reports, '1500')),
    bankDeposits: Math.max(0, sumTrialAccountCodes(reports, ['1900', '1920', '1930', '1950'])),
    assetInventoryRows,
    sharesInvestmentRows,
    otherAssetRows,
    debtRows,
    assetsInventoryTotal,
    sharesInvestmentTotal,
    otherAssetsTotal,
    totalAssets,
    totalDebt,
    netWealth,
    reportedNetWealth: isEnk ? netWealth : Math.max(0, netWealth),
    valuationBasis: 'book', reviewRequired: true,
  };
};

const taxReturnAutomaticDifferences = (state, year, reports, taxBasis) => {
  const process = state.taxReturnProcessByYear?.[String(year)] || {};
  const automaticPermanentDifferences = window.PlummeTaxReturnReadiness.automaticPermanentDifferences(
    (code) => trialAccountValue(reports, code), taxBasis.permanentDifferences, year,
    (code) => trialAccount(state, code)?.name || '',
  );
  const automaticTemporaryDifferences = [
    fixedAssetTemporaryDifferenceRow(state, year),
    ...window.PlummeTaxReturnReadiness.automaticSpecificationDifferences(state, year, process),
  ].filter((entry)=>[entry.rmFjor,entry.smFjor,entry.rmAar,entry.smAar].some((value)=>Math.abs(num(value))>0.005));
  return { automaticPermanentDifferences, automaticTemporaryDifferences };
};

const buildTaxReturnModel = (options = {}) => {
  if (!runtime.appState || (!runtime.configured && !runtime.user)) return null;
  const state = mergeState(runtime.appState);
  const year = Number(options.year || state.activeYear) || new Date().getFullYear();
  const reports = cachedYearReports(state, year);
  const company = buildCompany(state);
  const companyType = settingsCompanyType(state.settings);
  const isEnk = isEnkCompanyType(companyType);
  const accountingScope = resolveAccountingScope(state.settings, year);
  const resultBeforeTax = num(buildAnnualAccountsModel({ year })?.incomeStatement?.resultBeforeTax);
  const process = state.taxReturnProcessByYear?.[String(year)] || {};
  const normalizedOther = normalizeTaxOtherModel(process.taxOther || {});
  const companyInfo = normalizedOther.companyInfo || {};
  const ordinaryCompanyTax = companyInfo.industryRegime === 'ordinary' && companyInfo.financeTaxApplicability === 'not_applicable';
  const taxRate = accountingScope.jurisdiction === 'NO' && accountingScope.entityFamily === 'limited_company' && ordinaryCompanyTax
    ? norwegianCorporateTaxRate(year)
    : null;
  const importedSubmitted = importedComplianceDelivered(state, year, 'taxReturn');
  const normalizedBasis = normalizeTaxBasisModel(process.taxBasis || {});
  const expectedPriorLoss = window.PlummeTaxReturnReadiness.priorLoss(state, year);
  const resolvedLoss = expectedPriorLoss !== null && normalizedBasis.lossCarryforward.openingSource !== 'manual'
    ? { ...normalizedBasis.lossCarryforward, fremfortTidl: expectedPriorLoss, openingConfirmed: true, openingSource: 'prior_year' }
    : normalizedBasis.lossCarryforward;
  const taxBasis = { ...normalizedBasis, lossCarryforward: resolvedLoss };
  const { automaticPermanentDifferences, automaticTemporaryDifferences } = taxReturnAutomaticDifferences(state, year, reports, taxBasis);
  const taxBasisCalculations = window.PlummeTaxBasisCalculation.calculate({ ...taxBasis, permanentDifferences: [...taxBasis.permanentDifferences, ...automaticPermanentDifferences], temporaryDifferences: [...taxBasis.temporaryDifferences, ...automaticTemporaryDifferences] }, resultBeforeTax, taxRate);
  const taxableBase = taxBasisCalculations.taxable;
  const lossCarryforward = taxBasisCalculations.fremfortInntekt;
  const taxAmount = taxBasisCalculations.taxAmount;
  // Company tax applies the whole eligible prior loss; store the calculated use so the draft never carries a stale request.
  const taxBasisOut = taxBasisCalculations.lossUseCalculated ? { ...taxBasis, lossCarryforward: { ...taxBasis.lossCarryforward, aaretsAnv: taxBasisCalculations.lossApplied } } : taxBasis;
  const notCalculatedInputs = window.PlummeTaxBasisCalculation.notCalculatedInputs({ taxBasis, taxAssets: process.taxAssets || {} });
  const taxAssets = taxReturnAssetsModel(state, year, process.taxAssets || {});
  const wealth = { ...taxReturnWealthModel(reports, isEnk), fixedAssetBasis: window.PlummeFixedAssetRegister.differences(state, year) };
  wealth.valuation = window.PlummeTaxWealth.build({ rows: trialAccountRows(reports), scope: accountingScope, year, fixedAssetBasis: wealth.fixedAssetBasis, valuationFacts: { taxOther: process.taxOther || {}, taxAssets: process.taxAssets || {}, taxIncomeBalance: process.taxIncomeBalance || {} }, declaration: process.taxWealth });
  const taxOther = taxReturnOtherModel(state, process.taxOther || {}, wealth);
  const taxIncomeBalance = normalizeTaxIncomeBalanceModel(process.taxIncomeBalance || {});
  const taxSettlement = window.PlummeTaxReturnReadiness.normalizeSettlement(process.taxSettlement || {});
  const processSubmission = normalizeTaxSubmissionModel(process.submission || {});
  const contact = taxReturnContactModel(state, company);
  const auditorRequired = Boolean(
    state.settings?.auditorRequired ||
    state.settings?.hasAuditor ||
    state.settings?.auditorName ||
    state.settings?.auditorOrgNumber
  );
  const processModel = {
    ...process,
    taxBasis: taxBasisOut,
    taxOther,
    taxSettlement,
    confirmedStepIds: importedSubmitted ? IMPORTED_TAX_RETURN_STEP_IDS : safeArray(process.confirmedStepIds).map(String).filter(Boolean),
  };
  const readiness = window.PlummeTaxReturnReadiness.readiness({ state, year, process: processModel, scope: accountingScope, wealthValuation: wealth.valuation });
  const scopeReviewRequired = !['company', 'personal_business'].includes(accountingScope.taxRegime) || accountingScope.productSupport !== 'partial';
  return {
    year,
    company,
    companyType: accountingScope.legalFormCode,
    isEnk,
    accountingScope,
    reviewRequired: scopeReviewRequired || (accountingScope.entityFamily === 'limited_company' && !ordinaryCompanyTax) || notCalculatedInputs.length > 0,
    notCalculatedInputs,
    taxProvisionStatus: process.taxProvisionStatus || null,
    readiness,
    contact,
    submission: {
      period: `01.01-31.12.${year}`,
      accountingRuleType: accountingScope.accountingFramework,
      auditor: auditorRequired ? 'Ja' : 'Nei',
      submitter: contact.navn,
      taxableLabel: [company.name, company.orgnr].filter(Boolean).join(' - '),
      ...processSubmission,
    },
    summary: {
      income: num(reports?.result?.inntekter),
      costs: num(reports?.result?.kostnader),
      resultBeforeTax,
      taxableBase,
      netBusinessIncome: accountingScope.entityFamily === 'sole_trader' ? taxBasisCalculations.taxableBeforeLoss : null,
      lossCarryforward,
      taxRate,
      taxAmount,
    },
    taxBasis: {
      ...taxBasisOut,
      automaticPermanentDifferences,
      automaticTemporaryDifferences,
      calculations: taxBasisCalculations,
    },
    taxAssets,
    wealth,
    taxOther,
    taxIncomeBalance,
    taxSettlement: {
      ...taxSettlement,
      calculatedTax: taxAmount,
      prepaidTotal: num(taxSettlement.advanceTaxPaid) + num(taxSettlement.additionalAdvanceTax),
      estimatedDifference: taxAmount == null ? null : Math.round((num(taxSettlement.advanceTaxPaid) + num(taxSettlement.additionalAdvanceTax) - taxAmount) * 100) / 100,
      finalDifference: taxSettlement.finalAssessedTax == null ? null : Math.round((num(taxSettlement.advanceTaxPaid) + num(taxSettlement.additionalAdvanceTax) - num(taxSettlement.finalAssessedTax)) * 100) / 100,
    },
    status: {
      yearClosed: Boolean(state.closedYearsByYear?.[String(year)] || state.yearClosingProcessByYear?.[String(year)]?.closedAt || state.deadlinesByYear?.[String(year)]?.annualClosingSubmitted),
      submitted: Boolean(state.deadlinesByYear?.[String(year)]?.taxSubmitted || process.submittedAt),
    },
    process: {
      phase: importedSubmitted ? 'draft' : (process.phase || 'intro'),
      submissionStage: importedSubmitted ? 'done' : (process.submissionStage || 'review'),
      activeStepId: importedSubmitted ? 'all' : process.activeStepId,
      confirmedStepIds: importedSubmitted
        ? IMPORTED_TAX_RETURN_STEP_IDS
        : safeArray(process.confirmedStepIds).map(String).filter(Boolean),
      taxSettlement,
      taxProvisionStatus: process.taxProvisionStatus || null,
      startedAt: process.startedAt,
      updatedAt: process.updatedAt,
    },
    source: 'backend',
  };
};

const isAccountingYearClosedForYear = (state, year) => {
  const yearKey = String(Number(year) || '');
  const deadlineStatus = state?.deadlinesByYear?.[yearKey] || {};
  const process = state?.yearClosingProcessByYear?.[yearKey] || {};
  return Boolean(state?.closedYearsByYear?.[yearKey] || process.closedAt || deadlineStatus.annualClosingSubmitted);
};

const saveTaxReturnProgress = async ({ year, patch = {} } = {}) => {
  const yearKey = String(Number(year || runtime.appState?.activeYear) || new Date().getFullYear());
  const now = nowIso();
  const state = mergeState(runtime.appState);
  const yearClosed = isAccountingYearClosedForYear(state, yearKey);
  if (!yearClosed && ['processing', 'done'].includes(String(patch.submissionStage || ''))) {
    throw new Error(t('clo.yearClosingRequiredBeforeFiling', { yearKey }));
  }
  const current = state.taxReturnProcessByYear?.[yearKey] || {};
    const nextTaxBasis = patch.taxBasis
      ? normalizeTaxBasisModel({ ...(current.taxBasis || {}), ...patch.taxBasis })
      : current.taxBasis;
    const nextTaxAssets = patch.taxAssets
      ? normalizeTaxAssetsModel({ ...(current.taxAssets || {}), ...patch.taxAssets })
      : current.taxAssets;
    const nextTaxOther = patch.taxOther
      ? normalizeTaxOtherModel({ ...(current.taxOther || {}), ...patch.taxOther })
      : current.taxOther;
    const nextTaxIncomeBalance = patch.taxIncomeBalance
      ? normalizeTaxIncomeBalanceModel({ ...(current.taxIncomeBalance || {}), ...patch.taxIncomeBalance })
      : current.taxIncomeBalance;
    const nextSubmission = patch.submission
      ? normalizeTaxSubmissionModel({ ...(current.submission || {}), ...patch.submission })
      : current.submission;
    const nextTaxSettlement = patch.taxSettlement
      ? window.PlummeTaxReturnReadiness.normalizeSettlement({ ...(current.taxSettlement || {}), ...patch.taxSettlement })
      : current.taxSettlement;
  const process = stripUndefinedDeep({
          ...current,
          phase: ['intro', 'details', 'draft'].includes(patch.phase) ? patch.phase : (current.phase || 'draft'),
          submissionStage: ['review', 'checked', 'ready', 'processing', 'done'].includes(patch.submissionStage) ? patch.submissionStage : (current.submissionStage || 'review'),
          startedAt: current.startedAt || now,
          updatedAt: now,
          activeStepId: typeof patch.activeStepId === 'string' ? patch.activeStepId : current.activeStepId,
          confirmedStepIds: patch.confirmedStepIds !== undefined
            ? Array.from(new Set(safeArray(patch.confirmedStepIds).map(String).filter(Boolean)))
            : safeArray(current.confirmedStepIds).map(String),
          submission: nextSubmission,
          taxBasis: nextTaxBasis,
          taxAssets: nextTaxAssets,
          taxOther: nextTaxOther,
          taxIncomeBalance: nextTaxIncomeBalance,
          taxSettlement: nextTaxSettlement,
          taxWealth: patch.taxWealth === undefined ? current.taxWealth : patch.taxWealth,
  });
  const result = await authorizedPost('taxReturnMutationHttp', {
    businessId: runtime.activeBusinessId,
    year: Number(yearKey),
    process,
  });
  if (result.state) runtime.appState = mergeAppMutationState(result.state);
  emitRuntime();
  return getTaxReturnModel({ year: Number(yearKey) });
};

const saveAssistantTaxAddition = async ({ year, proposalId, addition } = {}) => {
  const yearKey = String(Number(year || runtime.appState?.activeYear) || new Date().getFullYear());
  const result = await authorizedPost('taxReturnMutationHttp', {
    businessId: runtime.activeBusinessId,
    year: Number(yearKey),
    action: 'add_assistant_tax_adjustment',
    proposalId,
    addition,
  });
  if (result.state) runtime.appState = mergeAppMutationState(result.state);
  emitRuntime();
  return getTaxReturnModel({ year: Number(yearKey) });
};
